import { translateDepartment } from './translationUtils';

export const COMMON_DEPARTMENTS = [
  'Administration',
  'Customer Support',
  'Engineering',
  'Product Management',
  'Design / UX',
  'Data & Analytics',
  'Marketing',
  'Sales',
  'Finance & Accounting',
  'Human Resources',
  'Operations',
  'IT / Systems',
  'Security / InfoSec',
  'Legal',
  'Compliance',
  'Procurement',
  'Supply Chain',
  'Logistics',
  'Manufacturing',
  'Quality Assurance',
  'Research & Development',
  'Business Development',
  'Partnerships',
  'Strategy',
  'Content',
  'Communications / PR',
  'Community',
  'Facilities',
  'Real Estate',
  'Construction',
  'Healthcare',
  'Education'
];

export const COMMUNITY_CHANNEL_OPTIONS = [
  { value: 'talent_pool', label: 'Talent Pool' },
  { value: 'workforce', label: 'Team Updates' }
];

export const COMMUNITY_FEATURE_SESSION_EVENT = 'community-feature-session-change';
export const TEAM_ACCESS_PROFILE_STORAGE_EVENT = 'team-access-profile-storage-change';

const getCommunityFeatureUserSessionCacheKey = (userId = '') => {
  const normalizedUserId = String(userId || '').trim();
  return normalizedUserId ? `community-feature:${normalizedUserId}` : '';
};

export function getCommunityFeatureSessionCacheKey(userId = '', companyUID = '') {
  const normalizedUserId = String(userId || '').trim();
  const normalizedCompanyUID = String(companyUID || '').trim();

  if (!normalizedUserId || !normalizedCompanyUID) {
    return '';
  }

  return `community-feature:${normalizedUserId}:${normalizedCompanyUID}`;
}

export function readCommunityFeatureSessionCache(userId = '', companyUID = '') {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return null;
  }

  const cacheKey = getCommunityFeatureSessionCacheKey(userId, companyUID);
  const userCacheKey = getCommunityFeatureUserSessionCacheKey(userId);

  const readFromKey = (key) => {
    if (!key) {
      return null;
    }

    const rawValue = window.sessionStorage.getItem(key);
    if (!rawValue) {
      return null;
    }

    const parsed = JSON.parse(rawValue);
    if (typeof parsed?.communityEnabled !== 'boolean') {
      return null;
    }

    return parsed.communityEnabled;
  };

  if (!cacheKey && !userCacheKey) {
    return null;
  }

  if (!cacheKey) {
    return readFromKey(userCacheKey);
  }

  try {
    const scopedValue = readFromKey(cacheKey);
    if (typeof scopedValue === 'boolean') {
      return scopedValue;
    }

    return readFromKey(userCacheKey);
  } catch (error) {
    console.error('Error reading community feature session cache:', error);
    return null;
  }
}

export function writeCommunityFeatureSessionCache(userId = '', companyUID = '', communityEnabled = null) {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return;
  }

  const cacheKey = getCommunityFeatureSessionCacheKey(userId, companyUID);
  const userCacheKey = getCommunityFeatureUserSessionCacheKey(userId);
  if (!cacheKey) {
    if (!userCacheKey) {
      return;
    }
  }

  try {
    if (typeof communityEnabled !== 'boolean') {
      if (cacheKey) {
        window.sessionStorage.removeItem(cacheKey);
      }
      if (userCacheKey) {
        window.sessionStorage.removeItem(userCacheKey);
      }
      window.dispatchEvent(
        new CustomEvent(COMMUNITY_FEATURE_SESSION_EVENT, {
          detail: {
            userId: String(userId || '').trim(),
            companyUID: String(companyUID || '').trim(),
            communityEnabled: null
          }
        })
      );
      return;
    }

    const payload = JSON.stringify({
      companyUID: String(companyUID || '').trim(),
      communityEnabled
    });
    if (cacheKey) {
      window.sessionStorage.setItem(cacheKey, payload);
    }
    if (userCacheKey) {
      window.sessionStorage.setItem(userCacheKey, payload);
    }
    window.dispatchEvent(
      new CustomEvent(COMMUNITY_FEATURE_SESSION_EVENT, {
        detail: {
          userId: String(userId || '').trim(),
          companyUID: String(companyUID || '').trim(),
          communityEnabled
        }
      })
    );
  } catch (error) {
    console.error('Error writing community feature session cache:', error);
  }
}

const getTeamAccessProfileUserStorageCacheKey = (userId = '') => {
  const normalizedUserId = String(userId || '').trim();
  return normalizedUserId ? `team-access-profile:${normalizedUserId}` : '';
};

export function getTeamAccessProfileStorageCacheKey(userId = '', companyUID = '') {
  const normalizedUserId = String(userId || '').trim();
  const normalizedCompanyUID = String(companyUID || '').trim();

  if (!normalizedUserId || !normalizedCompanyUID) {
    return '';
  }

  return `team-access-profile:${normalizedUserId}:${normalizedCompanyUID}`;
}

export function readTeamAccessProfileStorageCache(userId = '', companyUID = '') {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null;
  }

  const cacheKey = getTeamAccessProfileStorageCacheKey(userId, companyUID);
  const userCacheKey = getTeamAccessProfileUserStorageCacheKey(userId);

  const readFromKey = (key) => {
    if (!key) {
      return null;
    }

    const rawValue = window.localStorage.getItem(key);
    if (!rawValue) {
      return null;
    }

    const parsed = JSON.parse(rawValue);
    if (!parsed || typeof parsed !== 'object' || !parsed.accessProfile) {
      return null;
    }

    const parsedCompanyUID = String(parsed.companyUID || '').trim();
    const requestedCompanyUID = String(companyUID || '').trim();
    if (requestedCompanyUID && parsedCompanyUID && parsedCompanyUID !== requestedCompanyUID) {
      return null;
    }

    return {
      companyUID: String(parsedCompanyUID || requestedCompanyUID).trim(),
      isCompanyOwner: parsed.isCompanyOwner === true,
      accessProfile: normalizeMemberRecord(parsed.accessProfile || {})
    };
  };

  if (!cacheKey && !userCacheKey) {
    return null;
  }

  try {
    if (cacheKey) {
      const scopedValue = readFromKey(cacheKey);
      if (scopedValue) {
        return scopedValue;
      }
    }

    return readFromKey(userCacheKey);
  } catch (error) {
    console.error('Error reading team access profile cache:', error);
    return null;
  }
}

export function writeTeamAccessProfileStorageCache(userId = '', companyUID = '', payload = null) {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }

  const cacheKey = getTeamAccessProfileStorageCacheKey(userId, companyUID);
  const userCacheKey = getTeamAccessProfileUserStorageCacheKey(userId);
  if (!cacheKey && !userCacheKey) {
    return;
  }

  try {
    if (!payload || typeof payload !== 'object') {
      if (cacheKey) {
        window.localStorage.removeItem(cacheKey);
      }
      if (userCacheKey) {
        window.localStorage.removeItem(userCacheKey);
      }
      window.dispatchEvent(
        new CustomEvent(TEAM_ACCESS_PROFILE_STORAGE_EVENT, {
          detail: {
            userId: String(userId || '').trim(),
            companyUID: String(companyUID || '').trim(),
            accessProfile: null
          }
        })
      );
      return;
    }

    const normalizedCompanyUID = String(payload.companyUID || companyUID || '').trim();
    const accessProfile = normalizeMemberRecord(payload.accessProfile || {});
    const cachePayload = JSON.stringify({
      companyUID: normalizedCompanyUID,
      isCompanyOwner: payload.isCompanyOwner === true,
      accessProfile,
      cachedAt: Date.now(),
      version: 1
    });

    if (cacheKey) {
      window.localStorage.setItem(cacheKey, cachePayload);
    }
    if (userCacheKey) {
      window.localStorage.setItem(userCacheKey, cachePayload);
    }

    window.dispatchEvent(
      new CustomEvent(TEAM_ACCESS_PROFILE_STORAGE_EVENT, {
        detail: {
          userId: String(userId || '').trim(),
          companyUID: normalizedCompanyUID,
          isCompanyOwner: payload.isCompanyOwner === true,
          accessProfile
        }
      })
    );
  } catch (error) {
    console.error('Error writing team access profile cache:', error);
  }
}

const COMMUNITY_CHANNEL_SET = new Set(COMMUNITY_CHANNEL_OPTIONS.map((option) => option.value));

export function buildCommunityChannelOptions(channelMap = {}) {
  const options = Object.entries(channelMap || {})
    .filter(([, value]) => value && value.enabled !== false)
    .map(([value, config]) => ({
      value,
      label: String(config?.label || value)
    }));

  if (options.length > 0) {
    return options.sort((left, right) => left.label.localeCompare(right.label));
  }

  return COMMUNITY_CHANNEL_OPTIONS;
}

export const OWNER_PERMISSIONS = {
  canPostJobs: true,
  canPublishJobs: true,
  canManageApplicants: true,
  canDraftOffers: true,
  canApproveOffers: true,
  canReviewJobs: true,
  canReviewOffers: true,
  canApprove: true,
  canManageOnboarding: true,
  canManageEmployees: true,
  canManagePayroll: true,
  canSubmitPayroll: true,
  canViewNationalization: true,
  canManageNationalization: true,
  canAccessAnalytics: true,
  canEditCommunity: true,
  canManageTeam: true,
  canManageCompanySettings: true
};

export const DEFAULT_COLLABORATOR_PERMISSIONS = {
  canPostJobs: true,
  canPublishJobs: false,
  canManageApplicants: true,
  canDraftOffers: true,
  canApproveOffers: false,
  canReviewJobs: false,
  canReviewOffers: false,
  canApprove: false,
  canManageOnboarding: false,
  canManageEmployees: false,
  canManagePayroll: false,
  canSubmitPayroll: false,
  canViewNationalization: false,
  canManageNationalization: false,
  canAccessAnalytics: false,
  canEditCommunity: false,
  canManageTeam: false,
  canManageCompanySettings: false
};

export const TEAM_PERMISSION_SECTIONS = [
  {
    id: 'recruitment',
    title: 'Recruitment workspace',
    description: 'Control job creation, applicant handling, and offer execution.',
    permissions: [
      {
        key: 'canPostJobs',
        title: 'Create job posts',
        description: 'Create and edit job drafts.'
      },
      {
        key: 'canPublishJobs',
        title: 'Publish jobs',
        description: 'Publish jobs directly without approval.'
      },
      {
        key: 'canManageApplicants',
        title: 'Manage applicants',
        description: 'Review applicants and move them through the pipeline.'
      },
      {
        key: 'canDraftOffers',
        title: 'Draft offers',
        description: 'Prepare offer letters and submit them for approval.'
      },
      {
        key: 'canApproveOffers',
        title: 'Send offers directly',
        description: 'Send offers directly when approvals are enabled.'
      }
    ]
  },
  {
    id: 'approvals',
    title: 'Approval authority',
    description: 'Decide who can review and approve shared workflow actions.',
    permissions: [
      {
        key: 'canReviewJobs',
        title: 'Job approver',
        description: 'Review and approve job posts submitted by others.'
      },
      {
        key: 'canReviewOffers',
        title: 'Offer approver',
        description: 'Review and approve offers submitted by others.'
      },
      {
        key: 'canApprove',
        title: 'Universal approver',
        description: 'Acts as a top-level approver across approval-enabled actions.'
      }
    ]
  },
  {
    id: 'operations',
    title: 'Operational access',
    description: 'Grant access to people operations, analytics, and admin controls.',
    permissions: [
      {
        key: 'canManageOnboarding',
        title: 'Manage onboarding',
        description: 'Work on onboarding flows, tasks, and coordination.'
      },
      {
        key: 'canManageEmployees',
        title: 'Manage employee records',
        description: 'Access workforce records and people operations details.'
      },
      {
        key: 'canManagePayroll',
        title: 'Prepare payroll',
        description: 'Access company payroll amounts, bank details and monthly reports. Requires access to all departments.'
      },
      {
        key: 'canSubmitPayroll',
        title: 'Submit payroll to banks',
        description: 'Connect a bank and send employer-approved payroll sheets and notices.'
      },
      {
        key: 'canViewNationalization',
        titleKey: 'team.permissions.canViewNationalization.title',
        descriptionKey: 'team.permissions.canViewNationalization.description',
        title: 'View nationalization',
        description: 'View company-level nationalization results without opening raw employee records.'
      },
      {
        key: 'canManageNationalization',
        titleKey: 'team.permissions.canManageNationalization.title',
        descriptionKey: 'team.permissions.canManageNationalization.description',
        title: 'Manage nationalization',
        description: 'Edit regulatory profiles, reconcile official results, and manage compliance mappings.'
      },
      {
        key: 'canAccessAnalytics',
        title: 'Access analytics',
        description: 'Open reporting and analytics views.'
      },
      {
        key: 'canEditCommunity',
        title: 'Edit community',
        description: 'Create and manage updates for assigned community feeds.'
      },
      {
        key: 'canManageTeam',
        title: 'Manage team access',
        description: 'Invite teammates and edit collaborator access.'
      },
      {
        key: 'canManageCompanySettings',
        title: 'Manage company settings',
        description: 'Edit workspace-level settings and controls.'
      }
    ]
  }
];

export const TEAM_PERMISSION_PRESETS = [
  {
    id: 'recruiter',
    label: 'Recruiter',
    description: 'Owns sourcing, jobs, applicants, and offers.',
    permissions: {
      canPostJobs: true,
      canPublishJobs: false,
      canManageApplicants: true,
      canDraftOffers: true,
      canApproveOffers: false,
      canReviewJobs: false,
      canReviewOffers: false,
      canApprove: false,
      canManageOnboarding: true,
      canManageEmployees: false,
      canViewNationalization: false,
      canManageNationalization: false,
      canAccessAnalytics: true,
      canEditCommunity: false,
      canManageTeam: false,
      canManageCompanySettings: false
    }
  },
  {
    id: 'hiring_manager',
    label: 'Hiring Manager',
    description: 'Reviews applicants and contributes inside assigned departments.',
    permissions: {
      canPostJobs: false,
      canPublishJobs: false,
      canManageApplicants: true,
      canDraftOffers: false,
      canApproveOffers: false,
      canReviewJobs: true,
      canReviewOffers: true,
      canApprove: false,
      canManageOnboarding: true,
      canManageEmployees: false,
      canViewNationalization: false,
      canManageNationalization: false,
      canAccessAnalytics: true,
      canEditCommunity: false,
      canManageTeam: false,
      canManageCompanySettings: false
    }
  },
  {
    id: 'approver',
    label: 'Approver',
    description: 'Reviews approvals and sees reporting without daily execution access.',
    permissions: {
      canPostJobs: false,
      canPublishJobs: false,
      canManageApplicants: false,
      canDraftOffers: false,
      canApproveOffers: false,
      canReviewJobs: true,
      canReviewOffers: true,
      canApprove: true,
      canManageOnboarding: false,
      canManageEmployees: false,
      canViewNationalization: false,
      canManageNationalization: false,
      canAccessAnalytics: true,
      canEditCommunity: false,
      canManageTeam: false,
      canManageCompanySettings: false
    }
  },
  {
    id: 'workspace_admin',
    label: 'Workspace Admin',
    description: 'Broad access across recruiting, people ops, and settings.',
    permissions: OWNER_PERMISSIONS
  }
];

const PERMISSION_KEYS = Object.keys(DEFAULT_COLLABORATOR_PERMISSIONS);

function splitNameParts(name = '') {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (parts.length === 0) {
    return { firstName: '', lastName: '' };
  }

  if (parts.length === 1) {
    return { firstName: parts[0], lastName: '' };
  }

  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(' ')
  };
}

export function buildMemberFullName(member = {}) {
  const firstName = String(member.firstName || '').trim();
  const lastName = String(member.lastName || '').trim();
  const combined = [firstName, lastName].filter(Boolean).join(' ').trim();

  if (combined) return combined;

  if (member.name) return String(member.name).trim();
  if (member.email) return String(member.email).trim();
  return '';
}

export function getMemberInitials(member = {}) {
  const firstName = String(member.firstName || '').trim();
  const lastName = String(member.lastName || '').trim();
  const fallbackName = buildMemberFullName(member);

  const letters = [firstName.charAt(0), lastName.charAt(0)]
    .filter(Boolean)
    .join('')
    .toUpperCase();

  if (letters) return letters;
  if (fallbackName) return fallbackName.slice(0, 2).toUpperCase();
  return 'TM';
}

export function normalizeDepartmentList(values = []) {
  const seen = new Set();
  return (Array.isArray(values) ? values : [values])
    .map((value) => String(value || '').replace(/\s+/g, ' ').trim())
    .filter(Boolean)
    .filter((value) => {
      const key = value.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

export function normalizeCommunityChannels(values = [], allowedValues = null) {
  const allowedSet = Array.isArray(allowedValues) && allowedValues.length > 0
    ? new Set(allowedValues.map((value) => String(value || '').trim()).filter(Boolean))
    : null;
  const seen = new Set();

  return (Array.isArray(values) ? values : [values])
    .map((value) => String(value || '').trim())
    .filter((value) => {
      if (!value) return false;
      if (allowedSet) return allowedSet.has(value);
      return COMMUNITY_CHANNEL_SET.has(value) || /^[a-z0-9]+(?:[_-][a-z0-9]+)*$/.test(value);
    })
    .filter((value) => {
      if (seen.has(value)) return false;
      seen.add(value);
      return true;
    });
}

export function normalizePermissionState(permissions = {}) {
  const merged = PERMISSION_KEYS.reduce((acc, key) => {
    acc[key] = permissions[key] === true;
    return acc;
  }, {});

  return applyPermissionDependencies({
    ...DEFAULT_COLLABORATOR_PERMISSIONS,
    ...merged
  });
}

export function applyPermissionDependencies(permissions = {}) {
  const next = { ...permissions };

  if (next.canSubmitPayroll) next.canManagePayroll = true;

  if (next.canPublishJobs) {
    next.canPostJobs = true;
  }

  if (!next.canPostJobs) {
    next.canPublishJobs = false;
  }

  if (next.canApproveOffers) {
    next.canDraftOffers = true;
  }

  if (!next.canDraftOffers) {
    next.canApproveOffers = false;
  }

  if (next.canApprove) {
    next.canReviewJobs = true;
    next.canReviewOffers = true;
  }

  if (!next.canReviewJobs || !next.canReviewOffers) {
    next.canApprove = false;
  }

  if (next.canManageCompanySettings) {
    next.canManageTeam = true;
  }

  // Managing legal profiles and official reconciliations necessarily includes
  // permission to see the aggregate result. Viewing alone never grants access
  // to raw employee records or nationalization setup controls.
  if (next.canManageNationalization) {
    next.canViewNationalization = true;
  }

  if (!next.canViewNationalization) {
    next.canManageNationalization = false;
  }

  return next;
}

export function togglePermission(permissions = {}, key) {
  const current = normalizePermissionState(permissions);
  return applyPermissionDependencies({
    ...current,
    [key]: !current[key]
  });
}

export function getPresetPermissions(presetId) {
  const preset = TEAM_PERMISSION_PRESETS.find((entry) => entry.id === presetId);
  return normalizePermissionState(preset?.permissions || DEFAULT_COLLABORATOR_PERMISSIONS);
}

export function getEnabledPermissionCount(permissions = {}) {
  const normalized = normalizePermissionState(permissions);
  return PERMISSION_KEYS.filter((key) => normalized[key]).length;
}

export function normalizeDepartmentScope(value, departments = []) {
  if (value === 'specific') return 'specific';
  if (normalizeDepartmentList(departments).length > 0) return 'specific';
  return 'all';
}

export function summarizeDepartmentAccess(member = {}, language = 'en') {
  const departments = normalizeDepartmentList(member.departments || []);
  const scope = normalizeDepartmentScope(member.departmentScope, departments);

  if (scope === 'all') return String(language || 'en').startsWith('ar') ? 'جميع الأقسام' : 'All departments';
  if (departments.length === 0) return String(language || 'en').startsWith('ar') ? 'لم يتم تعيين أقسام' : 'No departments assigned';

  const translated = departments.map((department) => translateDepartment(department, language));
  if (translated.length <= 2) return translated.join(', ');
  return `${translated.slice(0, 2).join(', ')} +${translated.length - 2}`;
}

export function hasDepartmentAccess(member = {}, department = '') {
  const departments = normalizeDepartmentList(member.departments || []);
  const scope = normalizeDepartmentScope(member.departmentScope, departments);
  const normalizedDepartment = String(department || '').replace(/\s+/g, ' ').trim().toLowerCase();

  if (scope === 'all') return true;
  if (!normalizedDepartment) return true;

  return departments.some((entry) => entry.toLowerCase() === normalizedDepartment);
}

export function filterCollectionByDepartmentAccess(collection = [], member = {}, getDepartment = (item) => item?.department) {
  return (Array.isArray(collection) ? collection : []).filter((item) => hasDepartmentAccess(member, getDepartment(item)));
}

export function normalizeMemberRecord(record = {}, fallback = {}) {
  const source = { ...fallback, ...record };
  const derivedName = splitNameParts(source.name || '');
  const firstName = String(source.firstName || derivedName.firstName || '').trim();
  const lastName = String(source.lastName || derivedName.lastName || '').trim();
  const departments = normalizeDepartmentList(
    source.departments || source.department || source.allowedDepartments || []
  );
  const permissions = normalizePermissionState(source.permissions || {});
  const communityChannels = permissions.canEditCommunity
    ? normalizeCommunityChannels(source.communityChannels || source.allowedCommunityChannels || [])
    : [];

  return {
    uid: source.uid || source.key || '',
    key: source.key || source.uid || '',
    sourceType: source.sourceType || 'collaborator',
    email: String(source.email || '').trim(),
    firstName,
    lastName,
    name: buildMemberFullName({
      firstName,
      lastName,
      name: source.name,
      email: source.email
    }),
    position: String(source.position || source.jobPosition || source.role || '').trim(),
    photoUrl: source.photoUrl || source.photoURL || '',
    departmentScope: normalizeDepartmentScope(source.departmentScope, departments),
    departments,
    permissions,
    communityChannels
  };
}

export function buildInvitePayload(form = {}) {
  const normalized = normalizeMemberRecord(form);
  return {
    email: normalized.email,
    firstName: normalized.firstName,
    lastName: normalized.lastName,
    name: normalized.name,
    position: normalized.position,
    departmentScope: normalized.departmentScope,
    departments: normalized.departments,
    permissions: normalized.permissions,
    communityChannels: normalized.communityChannels
  };
}
